import { put } from 'typed-redux-saga';
import { call, takeLatest, takeEvery } from 'redux-saga/effects';
import * as actions from './operational.action';
import {
	fetchReportsDetail,
	fetchIssueOverviewDetail,
	fetchLastMonthIssueOverviewDetail,
	fetchIssueChartData,
	fetchReportChartData,
	fetchTopList,
	fetchTrendsData,
} from 'services/dashboardRevamp/reports/operationalKPI.service';
import { fetchIssueList, fetchReportList } from 'services/dashboardRevamp/executiveDashboard/perfomanceCard.service';
import { GetCompleteReportDetail } from '@nimbly-technologies/nimbly-common';
import {
	IssueChartData,
	IssueOverviewDetail,
	LastMonthIssueOverviewDetail,
	IssueResolutionData,
	ReportChartData,
	ReportCompletionData,
} from './operationalStore';
import { IssueListI, ReportListI } from '../executive/executive.types';

function* fetchCompleteReportDetailSaga(
	action: ReturnType<typeof actions.fetchCompleteReportDetailAsync.request>,
): Generator {
	try {
		const res = (yield call(fetchReportsDetail) as unknown) as GetCompleteReportDetail;
		yield put(actions.fetchCompleteReportDetailAsync.success(res));
	} catch {
		yield put(actions.fetchCompleteReportDetailAsync.failure('[ERROR] Failed to get Dashboard Report Details'));
	}
}

function* fetchIssueOverviewDetailAsync(
	action: ReturnType<typeof actions.fetchIssueOverviewDetailAsync.request>,
): Generator {
	try {
		const res = (yield call(fetchIssueOverviewDetail) as unknown) as IssueOverviewDetail;
		yield put(actions.fetchIssueOverviewDetailAsync.success(res));
	} catch {
		yield put(actions.fetchIssueOverviewDetailAsync.failure('[ERROR] Failed to get Dashboard Issue Details'));
	}
}

function* fetchLastMonthIssueOverviewDetailAsync(
	action: ReturnType<typeof actions.fetchLastMonthIssueOverviewDetailAsync.request>,
): Generator {
	try {
		const res = (yield call(fetchLastMonthIssueOverviewDetail) as unknown) as LastMonthIssueOverviewDetail;
		yield put(actions.fetchLastMonthIssueOverviewDetailAsync.success(res));
	} catch {
		yield put(actions.fetchLastMonthIssueOverviewDetailAsync.failure('[ERROR] Failed to get Dashboard Issue Details'));
	}
}

function* fetchIssueChartAsync(action: ReturnType<typeof actions.fetchIssueChartAsync.request>): Generator {
	try {
		const res = (yield call(fetchIssueChartData) as unknown) as IssueChartData[];
		yield put(actions.fetchIssueChartAsync.success(res));
	} catch {
		yield put(actions.fetchIssueChartAsync.failure('[ERROR] Failed to get Dashboard Issues List'));
	}
}

function* fetchReportChartAsync(action: ReturnType<typeof actions.fetchReportChartAsync.request>): Generator {
	try {
		const res = (yield call(fetchReportChartData) as unknown) as ReportChartData[];
		yield put(actions.fetchReportChartAsync.success(res));
	} catch {
		yield put(actions.fetchReportChartAsync.failure('[ERROR] Failed to get Dashboard Issues List'));
	}
}

// IssueChart sagas for BasicDashboardPage
function* fetchIssueChartByCategorySaga(
	action: ReturnType<typeof actions.fetchIssueChartByCategoryAsync.request>,
): Generator {
	try {
		const res = (yield call(fetchIssueList, {
			sortDirection: 'desc',
			viewBy: 'openIssueCount',
			groupBy: 'category',
			sortBy: 'total',
			page: 1,
		}) as unknown) as IssueListI;
		yield put(actions.fetchIssueChartByCategoryAsync.success(res));
	} catch {
		yield put(actions.fetchIssueChartByCategoryAsync.failure('[ERROR] Failed to get Issue Chart by Category'));
	}
}

function* fetchIssueChartByQuestionnaireSaga(
	action: ReturnType<typeof actions.fetchIssueChartByQuestionnaireAsync.request>,
): Generator {
	try {
		const res = (yield call(fetchIssueList, {
			sortDirection: 'desc',
			viewBy: 'openIssueCount',
			groupBy: 'questionnaire',
			sortBy: 'total',
			page: 1,
		}) as unknown) as IssueListI;
		yield put(actions.fetchIssueChartByQuestionnaireAsync.success(res));
	} catch {
		yield put(
			actions.fetchIssueChartByQuestionnaireAsync.failure('[ERROR] Failed to get Issue Chart by Questionnaire'),
		);
	}
}

// SummaryView RCR sagas for BasicDashboardPage
function* fetchSummaryRCRBySiteSaga(action: ReturnType<typeof actions.fetchSummaryRCRBySiteAsync.request>): Generator {
	try {
		const res = (yield call(fetchReportList, {
			sortDirection: 'desc',
			viewBy: 'rcr',
			groupBy: 'site',
			sortBy: 'rcr',
			page: 1,
		}) as unknown) as ReportListI[];
		yield put(actions.fetchSummaryRCRBySiteAsync.success(res));
	} catch {
		yield put(actions.fetchSummaryRCRBySiteAsync.failure('[ERROR] Failed to get Summary RCR by Site'));
	}
}

function* fetchSummaryRCRByUserSaga(action: ReturnType<typeof actions.fetchSummaryRCRByUserAsync.request>): Generator {
	try {
		const res = (yield call(fetchReportList, {
			sortDirection: 'desc',
			viewBy: 'rcr',
			groupBy: 'user',
			sortBy: 'rcr',
			page: 1,
		}) as unknown) as ReportListI[];
		yield put(actions.fetchSummaryRCRByUserAsync.success(res));
	} catch {
		yield put(actions.fetchSummaryRCRByUserAsync.failure('[ERROR] Failed to get Summary RCR by User'));
	}
}

function* fetchSummaryRCRByDepartmentSaga(
	action: ReturnType<typeof actions.fetchSummaryRCRByDepartmentAsync.request>,
): Generator {
	try {
		const res = (yield call(fetchReportList, {
			sortDirection: 'desc',
			viewBy: 'rcr',
			groupBy: 'department',
			sortBy: 'rcr',
			page: 1,
		}) as unknown) as ReportListI[];
		yield put(actions.fetchSummaryRCRByDepartmentAsync.success(res));
	} catch {
		yield put(actions.fetchSummaryRCRByDepartmentAsync.failure('[ERROR] Failed to get Summary RCR by Department'));
	}
}

// SummaryView IRR sagas for BasicDashboardPage
function* fetchSummaryIRRBySiteSaga(action: ReturnType<typeof actions.fetchSummaryIRRBySiteAsync.request>): Generator {
	try {
		const res = (yield call(fetchIssueList, {
			sortDirection: 'desc',
			viewBy: 'irr',
			groupBy: 'site',
			sortBy: 'total',
			page: 1,
		}) as unknown) as IssueListI;
		yield put(actions.fetchSummaryIRRBySiteAsync.success(res));
	} catch {
		yield put(actions.fetchSummaryIRRBySiteAsync.failure('[ERROR] Failed to get Summary IRR by Site'));
	}
}

function* fetchSummaryIRRByUserSaga(action: ReturnType<typeof actions.fetchSummaryIRRByUserAsync.request>): Generator {
	try {
		const res = (yield call(fetchIssueList, {
			sortDirection: 'desc',
			viewBy: 'irr',
			groupBy: 'user',
			sortBy: 'total',
			page: 1,
		}) as unknown) as IssueListI;
		yield put(actions.fetchSummaryIRRByUserAsync.success(res));
	} catch {
		yield put(actions.fetchSummaryIRRByUserAsync.failure('[ERROR] Failed to get Summary IRR by User'));
	}
}

function* fetchSummaryIRRByDepartmentSaga(
	action: ReturnType<typeof actions.fetchSummaryIRRByDepartmentAsync.request>,
): Generator {
	try {
		const res = (yield call(fetchIssueList, {
			sortDirection: 'desc',
			viewBy: 'irr',
			groupBy: 'department',
			sortBy: 'total',
			page: 1,
		}) as unknown) as IssueListI;
		yield put(actions.fetchSummaryIRRByDepartmentAsync.success(res));
	} catch {
		yield put(actions.fetchSummaryIRRByDepartmentAsync.failure('[ERROR] Failed to get Summary IRR by Department'));
	}
}

// TopChart data saga for OperationalPage
function* fetchTopChartDataSaga(action: ReturnType<typeof actions.fetchTopChartDataAsync.request>): Generator {
	try {
		const { groupBy, tab } = action.payload;
		const res = (yield call(fetchTopList, { groupBy, tab }) as unknown) as any[];
		yield put(actions.fetchTopChartDataAsync.success({ tab, data: res }));
	} catch {
		yield put(actions.fetchTopChartDataAsync.failure('[ERROR] Failed to fetch Top Chart Data'));
	}
}

// Trends data saga for OperationalPage
function* fetchTrendsDataSaga(action: ReturnType<typeof actions.fetchTrendsDataAsync.request>): Generator {
	try {
		const { period, tab } = action.payload;
		const res = (yield call(fetchTrendsData, { period, tab }) as unknown) as any[];
		yield put(actions.fetchTrendsDataAsync.success({ tab, data: res }));
	} catch {
		yield put(actions.fetchTrendsDataAsync.failure('[ERROR] Failed to fetch Trends Data'));
	}
}

export default function* operationalDashboardSaga() {
	yield takeEvery(actions.fetchCompleteReportDetailAsync.request, fetchCompleteReportDetailSaga);
	yield takeEvery(actions.fetchIssueOverviewDetailAsync.request, fetchIssueOverviewDetailAsync);
	yield takeEvery(actions.fetchLastMonthIssueOverviewDetailAsync.request, fetchLastMonthIssueOverviewDetailAsync);
	yield takeEvery(actions.fetchIssueChartAsync.request, fetchIssueChartAsync);
	yield takeEvery(actions.fetchReportChartAsync.request, fetchReportChartAsync);

	// BasicDashboardPage IssueChart watchers
	yield takeEvery(actions.fetchIssueChartByCategoryAsync.request, fetchIssueChartByCategorySaga);
	yield takeEvery(actions.fetchIssueChartByQuestionnaireAsync.request, fetchIssueChartByQuestionnaireSaga);

	// BasicDashboardPage SummaryView RCR watchers
	yield takeEvery(actions.fetchSummaryRCRBySiteAsync.request, fetchSummaryRCRBySiteSaga);
	yield takeEvery(actions.fetchSummaryRCRByUserAsync.request, fetchSummaryRCRByUserSaga);
	yield takeEvery(actions.fetchSummaryRCRByDepartmentAsync.request, fetchSummaryRCRByDepartmentSaga);

	// BasicDashboardPage SummaryView IRR watchers
	yield takeEvery(actions.fetchSummaryIRRBySiteAsync.request, fetchSummaryIRRBySiteSaga);
	yield takeEvery(actions.fetchSummaryIRRByUserAsync.request, fetchSummaryIRRByUserSaga);
	yield takeEvery(actions.fetchSummaryIRRByDepartmentAsync.request, fetchSummaryIRRByDepartmentSaga);

	// OperationalPage TopChart & Trends - use takeLatest for deduplication
	yield takeLatest(actions.fetchTopChartDataAsync.request, fetchTopChartDataSaga);
	yield takeLatest(actions.fetchTrendsDataAsync.request, fetchTrendsDataSaga);
}
